import { useState } from 'react'

/** How far back from the caret an unclosed `{{` can start and still be completing. */
const TRIGGER_WINDOW = 40

export interface PlaceholderCompletion {
	/** Keys matching what has been typed since `{{`; empty when not completing. */
	matches: string[]
	/** Index of the highlighted match. */
	active: number
	/** Re-read the caret's context after an edit. */
	sync: (value: string, caret: number) => void
	/** Insert the chosen key as a closed `{{key}}`, returning the next text and caret. */
	accept: (value: string, caret: number, key: string) => { text: string; caret: number }
	/** Move the highlight; wraps at both ends. */
	move: (delta: number) => void
	/** Abandon the current completion. */
	dismiss: () => void
	/** Where the completion started, for positioning. */
	at: number | null
}

/**
 * `{{placeholder}}` completion over an arbitrary key list.
 *
 * Shared by the prompt body (completing over the prompt's variables) and an `each`
 * variable's item template (completing over that variable's own `itemFields`), which
 * are the same mechanism against different vocabularies. Keeping the key list a
 * parameter is what lets the second surface reuse the first — the alternative was a
 * near-copy that would drift on the next fix to either.
 *
 * Holds no DOM: the caller owns its input and decides where to draw the menu, since a
 * textarea needs a mirror-measured caret while a one-line input does not.
 */
export function usePlaceholderCompletion(keys: string[]): PlaceholderCompletion {
	const [query, setQuery] = useState<{ prefix: string; at: number } | null>(null)
	const [active, setActive] = useState(0)

	const matches =
		query === null ? [] : keys.filter((key) => key !== '' && key.toLowerCase().startsWith(query.prefix.toLowerCase()))

	const sync = (value: string, caret: number) => {
		const open = value.lastIndexOf('{{', caret)
		const prefix = open === -1 ? '' : value.slice(open + 2, caret)

		// Only an UNCLOSED `{{` within the window is a completion: a `}}` between the
		// braces and the caret means that placeholder is already finished, and a long
		// gap means the braces belong to earlier text.
		const completing = open !== -1 && caret - open <= TRIGGER_WINDOW && !prefix.includes('}') && !prefix.includes('{')

		setQuery(completing ? { prefix, at: open } : null)
		setActive(0)
	}

	const accept = (value: string, caret: number, key: string) => {
		if (query === null) return { text: value, caret }

		setQuery(null)

		return {
			text: `${value.slice(0, query.at)}{{${key}}}${value.slice(caret)}`,
			// After the inserted placeholder rather than at the end, so typing continues
			// where the author was.
			caret: query.at + key.length + 4,
		}
	}

	const move = (delta: number) => {
		setActive((index) => (index + delta + matches.length) % matches.length)
	}

	return { matches, active, sync, accept, move, dismiss: () => setQuery(null), at: query?.at ?? null }
}
